import React, { FunctionComponent } from 'react'
import { connect } from 'react-redux'
import numeral from 'numeral'
import {
  selectCurrentRefiVehicle,
  selectCurrentRefiUser,
  selectCoverageProducts,
  selectProducts,
  selectProductsSurcharges,
  selectSelectedGapProduct,
} from '../../reducers'
import { User, Vehicle } from '../../core/types'
import { Product, Surcharge } from '../refiTransactions/types'
import { isMonthlyMembershipOption } from '../../helpers/refiAppHelper'

export interface RefiSummaryProps {
  vehicle: Vehicle
  user: User
  selectedCoverageProducts: object
  products: Product[]
  productsSurcharges: Surcharge[]
  selectedGapProduct: Product
}

const trimName = (vehicle) => {
  const selectedTrim = vehicle?.selected_trim ?? vehicle?.selectedTrim

  if (selectedTrim !== null && selectedTrim !== undefined) {
    const base =
      selectedTrim.series ||
      selectedTrim.class_name ||
      selectedTrim.trim_name ||
      null

    if (base) {
      return selectedTrim.style ? `${base} ${selectedTrim.style}` : base
    }
  }

  return null
}

export const VehicleDetails = ({ vehicle }) => {
  if (!vehicle.id) return null
  const { year = '', make = '', model = '', mileage } = vehicle
  const trim =
    trimName(vehicle) || vehicle.trimSyncMetadata?.trim?.toUpperCase()

  return (
    <div className="refi-app-summary__section">
      <h4>VEHICLE DETAILS</h4>
      <hr className="refi-app-summary__hr" />
      <ul key="vehicleDetails">
        <li key="yearMakeModel">{`${year} ${make} ${model}`}</li>
        {trim ? <li key="trim">{trim}</li> : null}
        {mileage ? <li key="mileage">{`${mileage} miles`}</li> : null}
      </ul>
    </div>
  )
}

const UserDetails = ({ user }) => {
  if (!user.id) return null

  const { addresses } = user
  // Customers can exist without an address (e.g. created from a product package)
  const address = addresses?.at(-1)

  return (
    <div className="refi-app-summary__section">
      <h4>USER DETAILS</h4>
      <hr className="refi-app-summary__hr" />
      <ul key="userDetails">
        <li key="name">{`${user.lastName}, ${user.firstName}`}</li>
        <li key="email">
          {user.email || user.crmSyncMetadata?.unverified_email}
        </li>
        {address ? (
          <li key="address">{`${address.city}, ${address.state}`}</li>
        ) : null}
      </ul>
    </div>
  )
}

const CoverageDetails = ({
  products,
  productsSurcharges,
  selectedGapProduct,
  selectedCoverageProducts,
}) => {
  const { selectedOptions, selectedSurcharges = [] } = selectedCoverageProducts
  if (!products || (!selectedOptions.length && !selectedGapProduct)) return null

  const gapOption = selectedGapProduct && selectedGapProduct.options[0]

  const renderProductOptionDetails = (productOption) => {
    const {
      id,
      coverage_miles,
      coverage_months,
      deductible,
      productDescription,
    } = productOption

    // A monthly membership renews indefinitely, so its stored term (1) and
    // mileage (999999) are never shown -- UNLIMITED is a display concern.
    const monthlyMembership = isMonthlyMembershipOption(productOption)

    return (
      <ul key={`${id}-productOption`}>
        <li key={`${id}-productDescription`}>{productDescription}</li>
        {deductible && <li key={`${id}-deductible`}>{deductible}</li>}
        {coverage_months && (
          <li key={`${id}-coverage_months`}>
            {monthlyMembership ? 'UNLIMITED months' : `${coverage_months} months`}
          </li>
        )}
        {coverage_miles && (
          <li key={`${id}-coverage_miles`}>
            {monthlyMembership ? 'UNLIMITED miles' : `${coverage_miles} miles`}
          </li>
        )}
      </ul>
    )
  }

  return (
    <>
      <div className="refi-app-summary__section">
        <h4>COVERAGE DETAILS</h4>
        <hr className="refi-app-summary__hr" />
      </div>

      {gapOption && (
        <ul key={`${gapOption.id}-gapOption`}>
          <li key={`${gapOption.id}-gapOption`}>
            GAP Insurance +
            {numeral(gapOption.monthly_payment).format('$0,0.00')} / month (+
            {numeral(gapOption.total_cost).format('$0,0.00')} total)
          </li>
        </ul>
      )}

      {selectedOptions.map((option) => renderProductOptionDetails(option))}

      <ul>
        {selectedSurcharges.map((selectedSurchargeCode: string) => {
          const surcharge = productsSurcharges.find(
            (s: Surcharge) => s.SurchargeCode === selectedSurchargeCode
          )

          if (!surcharge) return null

          return <li key={selectedSurchargeCode}>{surcharge.Description}</li>
        })}
      </ul>
    </>
  )
}

export const RefiSummary: FunctionComponent<RefiSummaryProps> = ({
  vehicle,
  user,
  selectedCoverageProducts,
  products,
  productsSurcharges,
  selectedGapProduct,
}) => {
  return (
    <div>
      <h2>Application Summary</h2>
      <VehicleDetails vehicle={vehicle} />
      <UserDetails user={user} />
      <CoverageDetails
        products={products}
        productsSurcharges={productsSurcharges}
        selectedGapProduct={selectedGapProduct}
        selectedCoverageProducts={selectedCoverageProducts}
      />
    </div>
  )
}

export const mapStateToProps = (state) => ({
  vehicle: selectCurrentRefiVehicle(state),
  user: selectCurrentRefiUser(state),
  selectedCoverageProducts: selectCoverageProducts(state),
  products: selectProducts(state),
  productsSurcharges: selectProductsSurcharges(state),
  selectedGapProduct: selectSelectedGapProduct(state),
})

export default connect(mapStateToProps)(RefiSummary)
